import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import {
  X,
  FileText,
  User,
  Building,
  CreditCard,
  Info,
  Send,
  Sparkles,
  Zap,
} from "lucide-react";

// UI Components
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Textarea } from "components/ui/textarea";
import { MultiSelect } from "components/ui/multi-select";
import { DatePicker } from "components/ui/date-picker";
import { Toaster } from "components/ui/sonner";
import { toast } from "sonner";
import Tooltip from "components/ui/tooltip";

// Dialog Components
import NewPracticeDialog from "./components/NewPracticeDialog";
import AddHandlerDialog from "./components/AddHandlerDialog";

// Constants and Utils
import {
  INSURANCES_DEBUG,
  INSURANCES_PROD_DENTAL,
  NETWORK,
  NETWORK_OPTIONAL,
  INSURANCES_PROD_MEDICAL,
  ELIGIBILITY_SEARCH_QUERIES_DENTAL,
  ELIGIBILITY_SEARCH_QUERIES_MEDICAL,
  TEAM_BASED_ELIGIBILITY_SEARCH_QUERIES,
  REQUEST_TYPES_DENTAL,
  REQUEST_TYPES_MEDICAL,
  ELIGIBILITY_SEARCH_QUERIES_MENTAL_HEALTH,
  INSURANCES_PROD_MENTAL_HEALTH,
  Option,
  ELIGIBILITY_SEARCH_QUERIES_ALL,
  INSURANCES_PROD_MEDICAL_AND_DENTAL,
  ELIGIBILITY_SEARCH_QUERIES_MEDICAL_AND_DENTAL,
  TEAM_BASED_INSURANCES,
  PLACE_OF_SERVICE_OPTIONS,
  SPECIALIST_OPTIONS,
  TEAM_ID_BULK_CSV_ENABLED,
  SPECIALIST_OPTIONS_DENTAL,
  REQUEST_TYPES_CAMBER,
  ELIGIBILITY_SEARCH_QUERIES_COPILOT,
  INSURANCES_PROD_MEDICAL_COPILOT,
  REQUEST_TYPES_COPILOT,
} from "utils/constants";
import { TOOLTIPS } from "utils/tooltips";

// Actions and API
import { NEW_REQUEST_PAGE } from "utils/reducers";
import { Mixpanel } from "utils/Analytics";
import baseApi from "utils/axios-instance";
import { RequestSchema } from "utils/validationSchema";
import { RequestInfo } from "pages/MVP/RequestDetailsPage/components/RequestInfo";
import { ProfileInfo } from "utils/ProfileInfo";
import { CurrentUserInfo } from "pages/MVP/CurrentUserInfo";
import { isCamberWhitelabel } from "utils/utils";
import { convertToString, safeStringValue } from "utils/form-helpers";

interface NewRequestPageProps {
  reloadPage?: any;
  debugUser?: any;
  profileInfo?: ProfileInfo | undefined;
  currentUser?: CurrentUserInfo | undefined;
}

function NewRequestPage({
  reloadPage,
  debugUser,
  profileInfo,
  currentUser,
}: NewRequestPageProps) {
  const form = useForm({
    resolver: yupResolver(RequestSchema),
  });

  const { control, handleSubmit, watch, setValue } = form;

  const dispatch = useDispatch();

  // Watch for changes in the form
  const typeField = watch("type");
  const practiceIdField = watch("practice_id");
  const billingProviderIdField = watch("billing_provider_id");
  const renderingProviderIdField = watch("rendering_provider_id");
  const transferPhoneNumberField = watch("transfer_to_rep_number");

  // Watch all form values for localStorage saving
  const watchedValues = watch();

  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
  const [practicesOptionsList, setPracticesOptionsList] = useState<
    Array<Option>
  >([]);
  const [providersOptionsList, setProvidersOptionsList] = useState<
    Array<Option>
  >([]);
  const [showBulkMessage, setShowBulkMessage] = useState(true);
  const [useSameProvider, setUseSameProvider] = useState<boolean>(true);

  // Dialog states
  const [showNewPracticeDialog, setShowNewPracticeDialog] = useState(false);
  const [showAddHandlerDialog, setShowAddHandlerDialog] = useState(false);
  const [currentPracticeId, setCurrentPracticeId] = useState<string>("");

  // Unify prefill data source
  const [dataToPrefill, setDataToPrefill] = useState<
    Partial<RequestInfo> | undefined
  >();

  useEffect(() => {
    const savedDataJSON = localStorage.getItem("newRequestFormData");
    if (savedDataJSON) {
      const savedData = JSON.parse(savedDataJSON);
      setDataToPrefill(savedData);

      // Check if saved data has different providers
      if (
        savedData.billing_provider_id &&
        savedData.rendering_provider_id &&
        savedData.billing_provider_id !== savedData.rendering_provider_id
      ) {
        setUseSameProvider(false);
      }
    }
  }, []);

  // Save form data to localStorage whenever form values change
  useEffect(() => {
    if (Object.keys(watchedValues).length > 0) {
      // Filter out empty/undefined values
      const dataToSave = Object.fromEntries(
        Object.entries(watchedValues).filter(
          ([_, value]) => value !== undefined && value !== null && value !== ""
        )
      );

      if (Object.keys(dataToSave).length > 0) {
        localStorage.setItem("newRequestFormData", JSON.stringify(dataToSave));
      }
    }
  }, [watchedValues]);

  const handleSetPracticeID = useCallback((practice_id: string) => {
    setCurrentPracticeId(practice_id);
  }, []);

  // Clear conditional fields when type changes
  useEffect(() => {
    setValue("claims_date_of_service", undefined);
    setValue("billed_amount", undefined);
    setValue("eligibility_search_queries", undefined);
    setValue("place_of_service", undefined);
    setValue("is_specialist", undefined);
    setValue("insurance_in_network", undefined);
    setValue("diagnosis_codes", undefined);
    setValue("procedure_codes", undefined);
  }, [typeField, setValue]);

  // Prefill form with data from local storage
  useEffect(() => {
    if (!dataToPrefill) return;

    const allData = dataToPrefill as any;
    Object.keys(allData).forEach((key) => {
      const value = allData[key];
      if (value !== null && value !== undefined) {
        setValue(key as any, value, { shouldValidate: true });
      }
    });
  }, [dataToPrefill, setValue]);

  // Load practice options
  useEffect(() => {
    const loadPracticeOptions = (profileInfo: ProfileInfo | undefined) => {
      if (profileInfo == null) return;
      let practicesToLoad = profileInfo.practices;
      if (practicesToLoad == null) return;
      let practiceOptions: Array<Option> = [];
      if (practicesToLoad.length === 0)
        return setPracticesOptionsList(practiceOptions);
      practicesToLoad.forEach((val) => {
        practiceOptions.push({ label: val.name, value: val.id });
      });
      practiceOptions.push({ label: "Other", value: "OTHER" });
      setPracticesOptionsList(practiceOptions);
    };

    loadPracticeOptions(profileInfo);
  }, [practicesOptionsList.length, profileInfo]);

  // Load provider options
  useEffect(() => {
    const loadProviderOptions = (profileInfo: ProfileInfo | undefined) => {
      if (profileInfo == null) return;
      let practices = profileInfo.practices;
      if (practices == null) return;
      let providerOptions: Array<Option> = [];
      if (practices.length === 0)
        return setProvidersOptionsList(providerOptions);
      practices.forEach((val) => {
        if (val.id === practiceIdField) {
          val.providers.forEach((sub) => {
            providerOptions.push({ label: sub.name, value: sub.id });
          });
        }
      });
      providerOptions.push({ label: "Other", value: "OTHER" });
      setProvidersOptionsList(providerOptions);
    };
    loadProviderOptions(profileInfo);
  }, [practiceIdField, profileInfo, providersOptionsList.length]);

  // Handle new onboard modal trigger
  useEffect(() => {
    if (
      practiceIdField === "OTHER" ||
      billingProviderIdField === "OTHER" ||
      renderingProviderIdField === "OTHER"
    ) {
      handleSetPracticeID(practiceIdField);
      setShowNewPracticeDialog(true);
      // Reset the select field to avoid triggering again
      setValue("practice_id", "");
      setValue("billing_provider_id", "");
      setValue("rendering_provider_id", "");
    }
  }, [
    practiceIdField,
    billingProviderIdField,
    renderingProviderIdField,
    setValue,
    handleSetPracticeID,
  ]);

  // Handle transfer phone number modal trigger
  useEffect(() => {
    if (transferPhoneNumberField === "OTHER") {
      setShowAddHandlerDialog(true);
      // Reset the select field to avoid triggering again
      setValue("transfer_to_rep_number", "");
    }
  }, [transferPhoneNumberField, setValue]);

  // Clear eligibility search if type is not eligibility search
  useEffect(() => {
    if (typeField !== "ELIGIBILITY_SEARCH") {
      setValue("eligibility_search_queries", undefined);
    }
  }, [typeField, setValue]);

  // Handle "use same provider" checkbox
  useEffect(() => {
    if (useSameProvider && billingProviderIdField) {
      setValue("rendering_provider_id", billingProviderIdField);
    }
  }, [useSameProvider, billingProviderIdField, setValue]);

  // Update checkbox state when rendering provider is manually changed (but allow manual unchecking)
  useEffect(() => {
    if (
      renderingProviderIdField &&
      billingProviderIdField &&
      renderingProviderIdField === billingProviderIdField &&
      !useSameProvider
    ) {
      setUseSameProvider(true);
    }
  }, [renderingProviderIdField, billingProviderIdField, useSameProvider]);

  // Helper function to filter out empty string values from option arrays and remove duplicates
  const filterValidOptions = (options: Array<Option>) => {
    const filtered = options.filter((option) => option.value !== "");

    // Remove duplicates based on both value and label
    const uniqueOptions = filtered.filter(
      (option, index, self) =>
        index ===
        self.findIndex(
          (o) => o.value === option.value && o.label === option.label
        )
    );

    return uniqueOptions;
  };

  // Helper function to format date as MM-DD-YYYY for API
  const formatDateForAPI = (date: Date): string => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  // Helper function to parse date from MM-DD-YYYY or YYYY-MM-DD format
  const parseDateValue = (dateValue: string): Date | undefined => {
    if (!dateValue) return undefined;

    // Handle both MM-DD-YYYY and YYYY-MM-DD formats
    if (dateValue.includes("-")) {
      const parts = dateValue.split("-");
      if (parts.length === 3) {
        // Check if it's YYYY-MM-DD (year first)
        if (parts[0].length === 4) {
          return new Date(`${dateValue}T00:00:00`);
        }
        // Otherwise it's MM-DD-YYYY (month first)
        else {
          const [month, day, year] = parts;
          return new Date(`${year}-${month}-${day}T00:00:00`);
        }
      }
    }

    return new Date(`${dateValue}T00:00:00`);
  };

  const onSubmit = async (data: any) => {
    setIsSubmitDisabled(true);
    if (data.insurance_in_network === "Unknown") {
      data.insurance_in_network = null;
    }

    try {
      await baseApi.post("/dashboard/new_inquiry", data, {
        withCredentials: true,
      });
      Mixpanel.track("New request submitted", { $request_type: data.type });
      toast.success("Request submitted successfully!");

      // Clear localStorage after successful submission
      localStorage.removeItem("newRequestFormData");

      // Navigate back to dashboard and refresh the table
      setTimeout(() => {
        dispatch({ type: NEW_REQUEST_PAGE.hide });
        if (reloadPage) reloadPage(true); // Reload with loading overlay
      }, 500);
    } catch (error: any) {
      Mixpanel.track("Error: New Request", { error: error, data: data });
      const message = error.response?.data?.detail || error.response?.data;
      console.warn(message);
      toast.error(message || "Failed to submit request");
    }
    setIsSubmitDisabled(false);
  };

  // Get options based on specialty using useMemo to prevent unnecessary re-computation
  const { eligibilitySearchOptions, requestTypes, insuranceOptions } =
    useMemo(() => {
      let eligibilitySearchOptions: Array<Option> = [];
      let requestTypes: Array<Option> = [];
      let insuranceOptions: Array<Option> = [];

      switch (profileInfo?.specialty) {
        case "MEDICAL":
          eligibilitySearchOptions = ELIGIBILITY_SEARCH_QUERIES_MEDICAL;
          requestTypes = REQUEST_TYPES_MEDICAL;
          insuranceOptions = INSURANCES_PROD_MEDICAL;
          break;
        case "DENTAL":
          eligibilitySearchOptions = ELIGIBILITY_SEARCH_QUERIES_DENTAL;
          requestTypes = REQUEST_TYPES_DENTAL;
          insuranceOptions = INSURANCES_PROD_DENTAL;
          break;
        case "MEDICAL_AND_DENTAL":
          eligibilitySearchOptions =
            ELIGIBILITY_SEARCH_QUERIES_MEDICAL_AND_DENTAL;
          requestTypes = REQUEST_TYPES_DENTAL;
          insuranceOptions = INSURANCES_PROD_MEDICAL_AND_DENTAL;
          break;
        case "MENTAL_HEALTH":
          eligibilitySearchOptions = ELIGIBILITY_SEARCH_QUERIES_MENTAL_HEALTH;
          insuranceOptions = INSURANCES_PROD_MENTAL_HEALTH;
          if (isCamberWhitelabel()) {
            requestTypes = REQUEST_TYPES_CAMBER;
          } else {
            requestTypes = REQUEST_TYPES_MEDICAL;
          }
          break;
        default:
          eligibilitySearchOptions = [];
      }

      // Override for copilot customers
      if (profileInfo?.is_copilot) {
        eligibilitySearchOptions = ELIGIBILITY_SEARCH_QUERIES_COPILOT;
        insuranceOptions = INSURANCES_PROD_MEDICAL_COPILOT;
        requestTypes = REQUEST_TYPES_COPILOT;
      }

      // Team-based overrides
      if (
        currentUser != null &&
        currentUser.team_id in TEAM_BASED_ELIGIBILITY_SEARCH_QUERIES
      ) {
        eligibilitySearchOptions =
          TEAM_BASED_ELIGIBILITY_SEARCH_QUERIES[
            currentUser.team_id as keyof typeof TEAM_BASED_ELIGIBILITY_SEARCH_QUERIES
          ];
      }

      if (currentUser != null && currentUser.team_id in TEAM_BASED_INSURANCES) {
        insuranceOptions =
          TEAM_BASED_INSURANCES[
            currentUser.team_id as keyof typeof TEAM_BASED_INSURANCES
          ];
      }

      // Debug user overrides
      if (currentUser != null && debugUser) {
        eligibilitySearchOptions = ELIGIBILITY_SEARCH_QUERIES_ALL;
        insuranceOptions = INSURANCES_DEBUG;
      }

      return { eligibilitySearchOptions, requestTypes, insuranceOptions };
    }, [
      profileInfo?.specialty,
      profileInfo?.is_copilot,
      currentUser,
      debugUser,
    ]);

  var isCsvUploadDownloadEnabled = false;
  if (
    currentUser != null &&
    (debugUser || TEAM_ID_BULK_CSV_ENABLED.includes(currentUser.team_id))
  ) {
    isCsvUploadDownloadEnabled = true;
  }

  var specialistOptions = SPECIALIST_OPTIONS;
  if (profileInfo?.specialty === "DENTAL") {
    specialistOptions = SPECIALIST_OPTIONS_DENTAL;
  }

  return (
    <div className="w-full h-full">
      <Toaster richColors position="top-center" />
      <div className="p-4 max-w-5xl mx-auto">
        {/* Compact Header */}
        <div className="mb-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="relative">
              <div className="p-2.5 bg-gradient-to-br from-[#005151] to-[#004040] rounded-lg shadow-lg">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full animate-pulse"></div>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                New Request
              </h1>
              <p className="text-sm text-gray-500">
                AI-powered healthcare inquiry
              </p>
            </div>
            <div className="ml-auto">
              <div className="flex items-center space-x-1 px-3 py-1 bg-emerald-50 rounded-full">
                <Zap className="h-3 w-3 text-emerald-600" />
                <span className="text-xs font-medium text-emerald-700">
                  Smart Form
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bulk CSV Message */}
        {isCsvUploadDownloadEnabled && showBulkMessage && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span className="text-sm text-blue-800">
                Bulk submissions available{" "}
                <Link
                  to="/dashboard/csv"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline hover:no-underline"
                >
                  here
                </Link>
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowBulkMessage(false)}
              className="h-6 w-6 p-0 hover:bg-blue-100"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Compact Form Grid */}
            <Card className="border border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <Building className="h-4 w-4 text-[#005151]" />
                  <CardTitle className="text-lg font-semibold text-gray-900">
                    Practice & Provider
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-4">
                  <FormField
                    control={control}
                    name="practice_id"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                          Practice
                          <Tooltip text={TOOLTIPS.PRACTICE} />
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger
                              className={`h-10 ${
                                fieldState.error
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                  : ""
                              }`}
                            >
                              <SelectValue placeholder="Select practice" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="max-h-64 overflow-y-auto">
                            {practicesOptionsList.map((option) => (
                              <SelectItem
                                key={convertToString(option.value)}
                                value={convertToString(option.value)}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage className="text-red-500 font-medium" />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={control}
                      name="billing_provider_id"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                            Billing Provider
                            <Tooltip text={TOOLTIPS.BILLING_PROVIDER} />
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                className={`h-10 ${
                                  fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                }`}
                              >
                                <SelectValue placeholder="Select provider" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="max-h-64 overflow-y-auto">
                              {providersOptionsList.map((option) => (
                                <SelectItem
                                  key={convertToString(option.value)}
                                  value={convertToString(option.value)}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-red-500 font-medium" />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={control}
                      name="rendering_provider_id"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                            Rendering Provider
                            <Tooltip text={TOOLTIPS.RENDERING_PROVIDER} />
                          </FormLabel>
                          <div className="space-y-3">
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id="useSameProvider"
                                checked={useSameProvider}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  setUseSameProvider(isChecked);
                                  if (!isChecked) {
                                    // Clear the rendering provider when unchecking
                                    setValue("rendering_provider_id", "");
                                  }
                                }}
                                className="h-4 w-4 text-[#005151] focus:ring-[#005151] border-gray-300 rounded"
                              />
                              <label
                                htmlFor="useSameProvider"
                                className="text-sm text-gray-600 cursor-pointer"
                              >
                                Same as billing provider
                              </label>
                            </div>
                            {!useSameProvider && (
                              <Select
                                onValueChange={field.onChange}
                                value={safeStringValue(field.value)}
                              >
                                <FormControl>
                                  <SelectTrigger
                                    className={`h-10 ${
                                      fieldState.error
                                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                        : ""
                                    }`}
                                  >
                                    <SelectValue placeholder="Select different provider" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className="max-h-64 overflow-y-auto">
                                  {providersOptionsList.map((option) => (
                                    <SelectItem
                                      key={convertToString(option.value)}
                                      value={convertToString(option.value)}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            {useSameProvider && (
                              <div className="h-10 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md flex items-center text-sm text-gray-500">
                                Will use the same provider as billing provider
                              </div>
                            )}
                          </div>
                          <FormMessage className="text-red-500 font-medium" />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Request Details Section */}
            <Card className="border border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-[#005151]" />
                  <CardTitle className="text-lg font-semibold text-gray-900">
                    Request Details
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <FormField
                    control={control}
                    name="type"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                          Request Type
                          <Tooltip text={TOOLTIPS.REQUEST_TYPE} />
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger
                              className={`h-10 ${
                                fieldState.error
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                  : ""
                              }`}
                            >
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="max-h-64 overflow-y-auto">
                            {filterValidOptions(requestTypes).map((option) => (
                              <SelectItem
                                key={convertToString(option.value)}
                                value={convertToString(option.value)}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage className="text-red-500 font-medium" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name="desired_completion_date"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                          Results needed by
                          <Tooltip text={TOOLTIPS.DESIRED_COMPLETION_DATE} />
                        </FormLabel>
                        <FormControl>
                          <DatePicker
                            value={
                              field.value
                                ? parseDateValue(field.value)
                                : undefined
                            }
                            onChange={(date) => {
                              if (date) {
                                // Format as MM-DD-YYYY for API
                                field.onChange(formatDateForAPI(date));
                              } else {
                                field.onChange("");
                              }
                            }}
                            placeholder="MM/DD/YYYY"
                            className="w-full"
                            error={!!fieldState.error}
                          />
                        </FormControl>
                        <FormMessage className="text-red-500 font-medium" />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Conditional Fields for Eligibility Search */}
                {typeField === "ELIGIBILITY_SEARCH" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <FormField
                      control={control}
                      name="place_of_service"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                            Place of Service
                            <Tooltip text={TOOLTIPS.PLACE_OF_SERVICE} />
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                className={`h-10 ${
                                  fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                }`}
                              >
                                <SelectValue placeholder="Select place" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="max-h-64 overflow-y-auto">
                              {filterValidOptions(PLACE_OF_SERVICE_OPTIONS).map(
                                (option) => (
                                  <SelectItem
                                    key={convertToString(option.value)}
                                    value={convertToString(option.value)}
                                  >
                                    {option.label}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-red-500 font-medium" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="is_specialist"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                            Specialist
                            <Tooltip text={TOOLTIPS.IS_SPECIALIST} />
                          </FormLabel>
                          <Select
                            onValueChange={(value) => {
                              if (value === "true") field.onChange(true);
                              else if (value === "false") field.onChange(false);
                              else field.onChange(value);
                            }}
                            value={field.value?.toString()}
                          >
                            <FormControl>
                              <SelectTrigger
                                className={`h-10 ${
                                  fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                }`}
                              >
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="max-h-64 overflow-y-auto">
                              {filterValidOptions(specialistOptions).map(
                                (option) => (
                                  <SelectItem
                                    key={convertToString(option.value)}
                                    value={convertToString(option.value)}
                                  >
                                    {option.label}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-red-500 font-medium" />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* Conditional Fields for Claims */}
                {typeField === "CLAIMS" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <FormField
                      control={control}
                      name="claims_date_of_service"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                            Date of Service
                            <Tooltip text={TOOLTIPS.CLAIMS_DATE_OF_SERVICE} />
                          </FormLabel>
                          <FormControl>
                            <DatePicker
                              value={
                                field.value
                                  ? parseDateValue(field.value)
                                  : undefined
                              }
                              onChange={(date) => {
                                if (date) {
                                  // Format as MM-DD-YYYY for API
                                  field.onChange(formatDateForAPI(date));
                                } else {
                                  field.onChange("");
                                }
                              }}
                              placeholder="MM/DD/YYYY"
                              className="w-full"
                              error={!!fieldState.error}
                            />
                          </FormControl>
                          <FormMessage className="text-red-500 font-medium" />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={control}
                      name="billed_amount"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                            Billed Amount
                            <Tooltip text={TOOLTIPS.BILLED_AMOUNT} />
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="$0.00"
                              className={`h-10 ${
                                fieldState.error
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                  : ""
                              }`}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-red-500 font-medium" />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* Eligibility Search Queries */}
                {typeField === "ELIGIBILITY_SEARCH" && (
                  <div className="mb-4">
                    <FormField
                      control={control}
                      name="eligibility_search_queries"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                            Search Queries
                            <Tooltip
                              text={TOOLTIPS.ELIGIBILITY_SEARCH_QUERIES}
                            />
                          </FormLabel>
                          <FormControl>
                            <MultiSelect
                              options={eligibilitySearchOptions.map(
                                (opt: Option) => ({
                                  label: opt.label,
                                  value: opt.value.toString(),
                                })
                              )}
                              selected={field.value || []}
                              onChange={field.onChange}
                              placeholder="Select queries..."
                              className={
                                fieldState.error
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                  : ""
                              }
                            />
                          </FormControl>
                          <FormMessage className="text-red-500 font-medium" />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* Procedure Codes for Prior Auth Status */}
                {typeField === "PRIOR_AUTH_STATUS" && (
                  <div className="mb-4">
                    <FormField
                      control={control}
                      name="procedure_codes"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                            Procedure Codes
                            <Tooltip text={TOOLTIPS.PROCEDURE_CODES} />
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Enter codes"
                              className={`h-10 ${
                                fieldState.error
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                  : ""
                              }`}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage className="text-red-500 font-medium" />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* Diagnosis Codes for Medical/Mental Health */}
                {typeField === "ELIGIBILITY_SEARCH" &&
                  (profileInfo?.specialty === "MEDICAL" ||
                    profileInfo?.specialty === "MENTAL_HEALTH") && (
                    <div className="mb-4">
                      <FormField
                        control={control}
                        name="diagnosis_codes"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                              Diagnosis Codes{" "}
                              <span className="text-gray-500">(optional)</span>
                              <Tooltip text={TOOLTIPS.DIAGNOSIS_CODES} />
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter codes"
                                className={`h-10 ${
                                  fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                }`}
                                {...field}
                                value={safeStringValue(field.value)}
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
              </CardContent>
            </Card>

            {/* Insurance & Patient Info - Combined */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <CreditCard className="h-4 w-4 text-[#005151]" />
                    <CardTitle className="text-lg font-semibold text-gray-900">
                      Insurance
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    <FormField
                      control={control}
                      name="insurance_name"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-800">
                            Insurance Name
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger
                                className={`h-10 ${
                                  fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                }`}
                              >
                                <SelectValue placeholder="Select insurance" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="max-h-64 overflow-y-auto">
                              {filterValidOptions(insuranceOptions).map(
                                (option) => (
                                  <SelectItem
                                    key={convertToString(option.value)}
                                    value={convertToString(option.value)}
                                  >
                                    {option.label}
                                  </SelectItem>
                                )
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-red-500 font-medium" />
                        </FormItem>
                      )}
                    />

                    {typeField === "ELIGIBILITY_SEARCH" && (
                      <FormField
                        control={control}
                        name="insurance_in_network"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800">
                              Coverage
                            </FormLabel>
                            <Select
                              onValueChange={(value) => {
                                if (value === "true") field.onChange(true);
                                else if (value === "false")
                                  field.onChange(false);
                                else if (value === "null") field.onChange(null);
                                else field.onChange(value);
                              }}
                              value={field.value?.toString()}
                            >
                              <FormControl>
                                <SelectTrigger
                                  className={`h-10 ${
                                    fieldState.error
                                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                      : ""
                                  }`}
                                >
                                  <SelectValue placeholder="Select coverage" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent className="max-h-64 overflow-y-auto">
                                {filterValidOptions(
                                  typeField === "ELIGIBILITY_SEARCH"
                                    ? NETWORK_OPTIONAL
                                    : NETWORK
                                ).map((option) => (
                                  <SelectItem
                                    key={convertToString(option.value)}
                                    value={convertToString(option.value)}
                                  >
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Patient Information */}
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-[#005151]" />
                    <CardTitle className="text-lg font-semibold text-gray-900">
                      Patient Info
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={control}
                        name="patient_name"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800">
                              Patient Name
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter name"
                                className={`h-10 ${
                                  fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                }`}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={control}
                        name="dob"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800">
                              Date of Birth
                            </FormLabel>
                            <FormControl>
                              <DatePicker
                                value={
                                  field.value
                                    ? parseDateValue(field.value)
                                    : undefined
                                }
                                onChange={(date) => {
                                  if (date) {
                                    // Format as MM-DD-YYYY for API
                                    field.onChange(formatDateForAPI(date));
                                  } else {
                                    field.onChange("");
                                  }
                                }}
                                placeholder="MM/DD/YYYY"
                                className="w-full"
                                error={!!fieldState.error}
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={control}
                        name="member_id"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800">
                              Member ID/SSN
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="ID or SSN"
                                className={`h-10 ${
                                  fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                }`}
                                {...field}
                                value={safeStringValue(field.value)}
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={control}
                        name="group_id"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-800">
                              Group ID
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Group ID"
                                className={`h-10 ${
                                  fieldState.error
                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                    : ""
                                }`}
                                {...field}
                                value={safeStringValue(field.value)}
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 font-medium" />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={control}
                      name="patient_address"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium text-gray-800">
                            Address{" "}
                            <span className="text-gray-500">(optional)</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Patient address"
                              className={`h-10 ${
                                fieldState.error
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                  : ""
                              }`}
                              {...field}
                              value={safeStringValue(field.value)}
                            />
                          </FormControl>
                          <FormMessage className="text-red-500 font-medium" />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Additional Information */}
            <Card className="border border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center space-x-2">
                  <Info className="h-4 w-4 text-[#005151]" />
                  <CardTitle className="text-lg font-semibold text-gray-900">
                    Additional Info
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <FormField
                  control={control}
                  name="additional_info"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-800 flex items-center">
                        Notes <span className="text-gray-500">(optional)</span>
                        <Tooltip text={TOOLTIPS.ADDITIONAL_INFO} />
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Any additional information..."
                          className={`min-h-[80px] resize-none ${
                            fieldState.error
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                              : ""
                          }`}
                          {...field}
                          value={safeStringValue(field.value)}
                        />
                      </FormControl>
                      <FormMessage className="text-red-500 font-medium" />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Submit Actions */}
            <div className="flex justify-end gap-3 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => dispatch({ type: NEW_REQUEST_PAGE.hide })}
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitDisabled}
                className="bg-gradient-to-r from-[#005151] to-[#004040] hover:from-[#004040] hover:to-[#003030] text-white px-8 shadow-lg"
              >
                {isSubmitDisabled ? (
                  <>
                    <Send className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>

        {/* New Practice/Provider Dialog */}
        <NewPracticeDialog
          open={showNewPracticeDialog}
          onOpenChange={setShowNewPracticeDialog}
          practiceId={currentPracticeId}
          refreshProfileInfo={() => {
            // Reload profile info if needed
            if (reloadPage) reloadPage(true);
          }}
          currentUser={currentUser}
        />

        {/* Add Handler Dialog */}
        <AddHandlerDialog
          open={showAddHandlerDialog}
          onOpenChange={setShowAddHandlerDialog}
          refreshProfileInfo={() => {
            // Reload profile info if needed
            if (reloadPage) reloadPage(true);
          }}
        />
      </div>
    </div>
  );
}

export default NewRequestPage;
